/**
 * /learn/prizes — gamification prize selection grid.
 *
 * Layout matches Figma `6472:77003`:
 *   • Top hero band — "Your current score" mini-card on the left, deadline
 *     pill in the middle, mascot on the right.
 *   • White rounded body card — heading, search + filters, prize grid, and
 *     the recent-claims row.
 */

import React, { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { GiftIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { EASE_OUT, slideUp } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import { PrizeSearchField, PrizeFiltersButton } from '@/components/prizes/PrizeFilters';
import { useImageUrl } from '../../services/uploads';
import { usePrizes } from '../../hooks/learner/usePrizes';
import { useLeaderboard } from '../../hooks/learner/useLeaderboard';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import { getPoints } from '../../api/me';
import type { LearnerPrize } from '../../types/learnerPrizes';
import PrizeClaimsRow from '../../components/learner/PrizeClaimsRow';
import PrizeClaimFlow from '../../components/learner/PrizeClaimFlow';
import ChatAvatar from '../../components/chat/ChatAvatar';
import terySquirrel from '../../../public/tery/tery_clock.png';


function isForbiddenError(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 403;
}

/** The admin-configured points-reset instant, in the learner's local time.
 *  Null (no reset scheduled / unparseable) hides the deadline pill. */
function deadlineLabel(nextResetAt: string | null | undefined): string | null {
  if (!nextResetAt) return null;
  const target = new Date(nextResetAt);
  if (Number.isNaN(target.getTime())) return null;
  // Spelled-out month — "05/08/2026" reads as May 8 to half the world.
  return format(target, "d MMMM yyyy 'at' h:mm aaa");
}

const LearnerPrizesPage: React.FC = () => {
  const { data: prizes, isLoading, isError, error } = usePrizes();
  const { data: points } = useQuery({
    queryKey: ['me', 'gamification', 'points'],
    queryFn: getPoints,
  });

  const [search, setSearch] = useState('');
  const [gender, setGender] = useState<('men' | 'women' | 'unisex')[]>([]);
  const [color, setColor] = useState<string[]>([]);
  const [claimTarget, setClaimTarget] = useState<LearnerPrize | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [scoreRange, setScoreRange] = useState<[number, number] | null>(null);

  const balance = points?.balance ?? 0;
  const forbidden = isForbiddenError(error);
  const disabled = forbidden || points?.enabled === false;
  // Growth is "leaderboard only": points + standings are real, but the shop
  // can't take claims — show the explainer instead of claimable prizes that
  // would error (the 2026-07-24 Growth prize-shop fix).
  // Product rule 2026-07-24: learners never see locked features or upgrade
  // copy — if this workspace's plan has no prize shop, the page simply isn't
  // part of their world (nav hides it; direct URLs bounce home).
  const shopHidden =
    forbidden ||
    points?.enabled === false ||
    (points as { prizes_enabled?: boolean } | undefined)?.prizes_enabled === false;

  const pointsLoaded = points !== undefined || forbidden;

  const filtered = useMemo(() => {
    const list = prizes ?? [];
    const needle = search.trim().toLowerCase();
    return list.filter((p) => {
      if (gender.length && !gender.includes((p.gender ?? 'unisex') as (typeof gender)[number]))
        return false;
      if (color.length && !(p.colors ?? []).some((c) => color.includes(c.toLowerCase())))
        return false;
      if (scoreRange && (p.price_points < scoreRange[0] || p.price_points > scoreRange[1])) {
        return false;
      }
      if (!needle) return true;
      return (
        p.title.toLowerCase().includes(needle) ||
        (p.description ?? '').toLowerCase().includes(needle)
      );
    });
  }, [prizes, search, gender, color, scoreRange]);

  if (pointsLoaded && shopHidden) {
    return <Navigate to="/learn/dashboard" replace />;
  }

  return (
    <motion.div {...slideUp} className="space-y-6">
      <PageHeader title="Points & Prizes" root={{ label: 'Home', to: '/learn/dashboard' }} />
      <PrizeHero
        balance={balance}
        disabled={disabled}
        deadline={deadlineLabel(
          (points as { next_reset_at?: string | null } | undefined)?.next_reset_at
        )}
      />

      <section
        className="relative rounded-3xl border border-border-primary/30 bg-surface-elevated shadow-md p-5 sm:p-8 space-y-6 -mt-10 z-10"
      >
        <header>
          <h2 className="text-xl sm:text-2xl font-bold text-primary-500 tracking-tight">
            Prizes available at the moment
          </h2>
        </header>

        {!disabled && (
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <PrizeSearchField value={search} onChange={setSearch} />
            <PrizeFiltersButton
              gender={gender}
              onGender={setGender}
              color={color}
              onColor={setColor}
              colors={Array.from(
                new Set(
                  (prizes ?? []).flatMap((p) => (p.colors ?? []).map((c) => c.toLowerCase()))
                )
              )}
              scoreRange={scoreRange}
              onScoreRange={setScoreRange}
              maxScore={Math.max(200, ...(prizes ?? []).map((p) => p.price_points))}
            />
          </div>
        )}

        {isLoading ? (
          <PrizeGridSkeleton />
        ) : isError ? (
          <p className="text-sm text-error-500 py-12 text-center">
            Couldn't load prizes. Please try again.
          </p>
        ) : filtered.length === 0 ? (
          <EmptyState hasFilter={!!search || !!gender || !!color || !!scoreRange} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {(showAll ? filtered : filtered.slice(0, 6)).map((prize, i) => (
                <PrizeCard
                  key={prize.id}
                  prize={prize}
                  balance={balance}
                  index={i}
                  onClaim={() => setClaimTarget(prize)}
                />
              ))}
            </div>
            {!showAll && filtered.length > 6 && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="inline-flex items-center gap-1 text-sm font-bold text-text-primary hover:text-primary-500 focus-ring rounded-full px-2 py-1 transition-colors duration-fast"
                >
                  View all
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            )}
          </>
        )}

        {!disabled && <PrizeClaimsRow />}
      </section>

      {/* "My Prize History" — big primary button under the card (Figma). */}
      {!disabled && (
        <div>
          <Link
            to="/learn/prizes/history"
            className="inline-flex items-center justify-center min-h-[52px] px-8 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-[15px] font-bold focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
          >
            My Prize History
          </Link>
        </div>
      )}

      {claimTarget && (
        <PrizeClaimFlow
          prize={claimTarget}
          balance={balance}
          isOpen={!!claimTarget}
          onClose={() => setClaimTarget(null)}
        />
      )}
    </motion.div>
  );
};

export default LearnerPrizesPage;

// ─── Hero ───────────────────────────────────────────────────────────────────

interface PrizeHeroProps {
  deadline: string | null;
  balance: number;
  disabled: boolean;
}

function PrizeHero({ balance, disabled, deadline }: PrizeHeroProps) {
  const identity = useCurrentChatIdentity();
  const { currentRank } = useLeaderboard(50);

  const name =
    [currentRank?.first_name, currentRank?.last_name].filter(Boolean).join(' ').trim() ||
    identity.fullName ||
    'You';
  const avatarUrl = currentRank?.avatar_url ?? identity.avatarUrl;
  const score = currentRank?.balance ?? balance;

  return (
    <section
      className="
        relative overflow-visible rounded-3xl pb-16 mt-8
        bg-[linear-gradient(110deg,rgb(var(--color-secondary-100))_0%,rgb(var(--color-primary-50))_55%,rgb(var(--color-primary-100))_100%)]
        dark:bg-[radial-gradient(circle_at_68%_30%,rgb(var(--color-primary-350))_0%,rgb(var(--color-primary-600))_45%,rgb(var(--color-primary-950))_100%)]
      "
      aria-label="Your Gamification Status"
    >
      {/* Mascot — anchored to the panel itself (Figma: head rises past the
          panel top, feet stay above the overlapping body card). */}
      <img
        src={terySquirrel}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="hidden sm:block absolute bottom-8 right-[4%] lg:right-[10%] w-[160px] lg:w-[185px] select-none pointer-events-none drop-shadow-[0_10px_20px_rgba(0,0,0,0.25)]"
      />

      <div className="relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8 px-5 sm:px-8 pt-7 pb-10">
        {/* Score card */}
        <div className="flex flex-col gap-3 shrink-0 z-10">
          <p className="text-sm font-bold text-text-primary">Your Current Points</p>
          <ScoreNode name={name} score={disabled ? null : score} avatarUrl={avatarUrl} />
        </div>

        {/* Deadline pill — sits just left of the mascot, per the frames. */}
        {deadline && (
          <div className="z-10 self-center md:self-auto md:ml-auto md:mr-[215px] lg:mr-[330px] xl:mr-[360px]">
            <div className="bg-primary-500 rounded-2xl px-5 py-4 max-w-[220px] text-center shadow-md">
              <p className="text-sm font-bold leading-snug">
                Claim your prizes by{' '}
                <span className="whitespace-nowrap">{deadline}</span> — that&apos;s when
                this period ends
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

interface ScoreNodeProps {
  name: string;
  score: number | null;
  avatarUrl: string | null;
}

function ScoreNode({ name, score, avatarUrl }: ScoreNodeProps) {
  return (
    <div className="relative pt-5 w-[200px]">
      <div className="absolute left-1/2 top-0 -translate-x-1/2 z-10 ring-4 ring-white rounded-full">
        <ChatAvatar src={avatarUrl} name={name} size={40} />
      </div>
      <div className="bg-surface-elevated rounded-xl px-4 pb-5 pt-10 flex flex-col items-center gap-1 text-center shadow-sm">
        <p className="text-sm font-semibold text-text-primary truncate w-full">{name}</p>
        <div className="flex items-center gap-1">
          <span className="text-base font-bold text-text-primary tabular-nums">
            {score == null ? '—' : score.toLocaleString()}
          </span>
          <StarIcon className="w-4 h-4 text-warning-500" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

// ─── Search & filters ───────────────────────────────────────────────────────

// ─── Prize card ─────────────────────────────────────────────────────────────

interface PrizeCardProps {
  prize: LearnerPrize;
  balance: number;
  index: number;
  onClaim: () => void;
}

function PrizeCard({ prize, balance, index, onClaim }: PrizeCardProps) {
  const reduceMotion = useReducedMotion();
  const [coverUrl] = useImageUrl(prize.media_urls[0] ?? null);
  const affordable = balance >= prize.price_points;

  return (
    <motion.article
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : index * 0.04 }}
      className="rounded-2xl border border-border-primary/20 bg-background-primary dark:bg-grey-800 p-3 flex flex-col gap-3"
    >
      <div className="relative aspect-square overflow-hidden rounded-xl bg-text-tertiary/5">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.18), rgb(var(--color-secondary) / 0.18))',
            }}
          />
        )}
      </div>

      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-text-primary line-clamp-1">{prize.title}</h3>
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-text-primary tabular-nums">
            {prize.price_points.toLocaleString()}
          </span>
          <StarIcon className="w-4 h-4 text-warning-500" aria-hidden="true" />
        </div>
      </div>

      <div className="flex items-center gap-2 mt-auto">
        <Link
          to={`/learn/prizes/${prize.id}/claim`}
          className="flex-1 inline-flex items-center justify-center h-8 px-3 rounded-full bg-secondary-500 hover:bg-secondary-600 text-xs font-bold active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
        >
          See details
        </Link>
        <button
          type="button"
          onClick={onClaim}
          className="flex-1 inline-flex items-center justify-center h-8 px-3 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 disabled:bg-primary-500/40 disabled:cursor-not-allowed text-xs font-bold active:scale-[0.97] disabled:active:scale-100 transition-[background-color,transform] duration-fast ease-out-strong"
        >
          Claim now
        </button>
      </div>

      {!affordable && (
        <p className="text-[11px] text-text-tertiary italic">
          {(prize.price_points - balance).toLocaleString()} more points to claim
        </p>
      )}
    </motion.article>
  );
}

// ─── Empty / disabled / skeleton states ─────────────────────────────────────

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="py-12 text-center space-y-2">
      <GiftIcon className="w-10 h-10 mx-auto text-text-tertiary" />
      <p className="text-sm font-semibold text-text-primary">
        {hasFilter ? 'No prizes match your filters' : 'No prizes yet'}
      </p>
      <p className="text-xs text-text-secondary">
        {hasFilter
          ? 'Try a different keyword or category.'
          : "Your team admin hasn't published any prizes. Check back later."}
      </p>
    </div>
  );
}


function PrizeGridSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="rounded-2xl border border-border-primary/10 p-3 space-y-3 animate-pulse"
        >
          <div className="aspect-square rounded-xl bg-text-tertiary/10" />
          <div className="h-3 w-3/4 rounded bg-text-tertiary/10" />
          <div className="h-3 w-1/3 rounded bg-text-tertiary/10" />
          <div className="flex gap-2">
            <div className="h-8 flex-1 rounded-lg bg-text-tertiary/10" />
            <div className="h-8 flex-1 rounded-lg bg-text-tertiary/10" />
          </div>
        </div>
      ))}
    </div>
  );
}
